import { useRef, useState, type FormEvent } from 'react';
import html2canvas from 'html2canvas';
import { LifeBuoy } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Textarea } from './ui/Textarea';
import { ModalFormActions } from './ui/ModalFormActions';
import { useToast } from './ui/Toast';
import { useAuth } from '../context/AuthContext';
import { useReportarProblema } from '../context/ReportarProblemaContext';
import { enviarReporteProblema } from '../utils/sentryFeedback';

export { ReportarProblemaProvider } from '../context/ReportarProblemaContext';

async function capturarPantalla(): Promise<Uint8Array | null> {
  const canvas = await html2canvas(document.body);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return null;
  return new Uint8Array(await blob.arrayBuffer());
}

export function ReportarProblemaModal() {
  const { usuario } = useAuth();
  const toast = useToast();
  const { isOpen, sentryEventId, cerrar: cerrarContext } = useReportarProblema();
  const [mensaje, setMensaje] = useState('');
  const [incluirCaptura, setIncluirCaptura] = useState(true);
  const [loading, setLoading] = useState(false);
  // Cierre protegido por el guard de cambios sin guardar (X, Escape, backdrop y Cancelar).
  const closeRef = useRef<(() => void) | null>(null);

  const cerrar = () => {
    setMensaje('');
    setIncluirCaptura(true);
    setLoading(false);
    cerrarContext();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!usuario || mensaje.trim().length === 0) return;
    setLoading(true);
    try {
      const screenshot = incluirCaptura ? await capturarPantalla() : null;
      await enviarReporteProblema({ mensaje: mensaje.trim(), screenshot, usuario, sentryEventId });
      toast.success('¡Gracias! Registramos tu reporte y lo revisaremos.');
      cerrar();
    } catch {
      toast.error('No se pudo enviar el reporte. Intenta de nuevo.');
      setLoading(false);
    }
  };

  const sinTexto = mensaje.trim().length === 0;
  // Abierto desde el toast de un error ya capturado: el reporte agrega el relato humano.
  const vieneDeUnError = Boolean(sentryEventId);

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={cerrar}
      closeRef={closeRef}
      title={vieneDeUnError ? 'Cuéntanos qué pasó' : 'Reportar un problema'}
      icon={<LifeBuoy className="w-6 h-6 text-blue-600" />}
      size="sm"
      loading={loading}
      hasUnsavedChanges={!sinTexto}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {vieneDeUnError && (
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
            El error ya nos llegó con el detalle técnico. Lo que falta es tu parte: qué
            estabas intentando hacer. Con eso lo reproducimos y lo arreglamos antes.
          </p>
        )}
        <Textarea
          id="reporte-problema"
          label={vieneDeUnError ? '¿Qué estabas haciendo cuando falló?' : 'Describe el problema con detalle'}
          required
          rows={5}
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          placeholder={
            vieneDeUnError
              ? 'Ej: intenté registrar a una miembro nueva y al guardar salió el error.'
              : 'Cuéntanos qué pasó, qué esperabas y dónde lo viste.'
          }
          maxLength={2000}
          disabled={loading}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={incluirCaptura}
            onChange={(e) => setIncluirCaptura(e.target.checked)}
            disabled={loading}
          />
          <span>Incluir una captura de la pantalla actual</span>
        </label>
        <ModalFormActions
          onCancel={() => closeRef.current?.()}
          loading={loading}
          disabled={sinTexto}
          submitLabel="Enviar reporte"
          loadingLabel="Enviando..."
        />
      </form>
    </DialogShell>
  );
}
